import { Button } from '@/components/shadcn/button'
import { Form } from '@/components/shadcn/form'
import { ScrollArea } from '@/components/shadcn/scroll-area'
import { SheetTrigger } from '@/components/shadcn/sheet'
import { cn } from '@/lib/utils'
import { SpinnerIosRegular } from '@fluentui/react-icons'
import { zodResolver } from '@hookform/resolvers/zod'
import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef } from 'react'
import { useForm, useWatch } from 'react-hook-form'
import { PinFilterMenu } from '..'
import { FieldRenderer } from '../../component-library/FormComponent/FieldRenderer'
import { FormValues, InferFormValues, JsonFormSchemaV2 } from '../../component-library/FormComponent/types'
import { cleanupFormData, createDefaultValues, generateZodSchema } from '../../component-library/FormComponent/utils'
import { isFieldVisible } from '../../component-library/FormComponent/fieldVisibility'
import { useConditionalRuleFields } from '../../component-library/FormComponent/useConditionalRuleFields'
import { TPageFilter } from '../types'
import { useAppPageState } from '../useAppPageState'

// Define a type for the imperative handle
export type FormContentHandle = {
  reset: () => void
}

interface UnPinnedFilterContentProps {
  schema: JsonFormSchemaV2
  submitHandler: (data: InferFormValues<JsonFormSchemaV2>) => void
  resetDefaults?: InferFormValues<JsonFormSchemaV2>
  closeSheetOnSubmit?: boolean
  className?: string
  pinFilter: (selectedF: TPageFilter, side: 'left' | 'right') => void
  unPinnedFilters: TPageFilter[]
  isSubmitting?: boolean
  /** Parent provides scroll (e.g. popover content with overflow-y-auto). */
  embedInPopover?: boolean
}

export const UnPinnedFilterContent = forwardRef<FormContentHandle, UnPinnedFilterContentProps>(function FilterContent(
  {
    schema,
    submitHandler,
    resetDefaults,
    closeSheetOnSubmit,
    className,
    pinFilter,
    unPinnedFilters,
    isSubmitting,
    embedInPopover = false,
  },
  ref
) {
  const { isConfiguringLayout } = useAppPageState()
  const { properties, required } = schema
  const zodSchema = generateZodSchema(properties, required || [])

  // Compute schema defaults
  const schemaDefaults = useMemo(() => createDefaultValues(properties), [properties])

  // Merge persisted values with schema defaults - persisted values take precedence
  // This ensures the form initializes with the correct values from session storage
  const initialValues = useMemo(() => {
    if (resetDefaults && Object.keys(resetDefaults).length > 0) {
      return { ...schemaDefaults, ...resetDefaults }
    }
    return schemaDefaults
  }, [schemaDefaults, resetDefaults])

  const form = useForm<FormValues>({
    resolver: zodResolver(zodSchema),
    defaultValues: initialValues,
  })
  const watchedValues = useWatch({ control: form.control })
  const visibleWhenMatchedIds = useConditionalRuleFields(properties, 'visibleWhen', form.control)

  const { reset } = form

  // Expose the reset method via useImperativeHandle
  useImperativeHandle(
    ref,
    () => ({
      reset: () => reset(schemaDefaults),
    }),
    [reset, schemaDefaults]
  )

  // Track the previous resetDefaults to detect changes after initial mount
  const prevResetDefaultsRef = useRef(resetDefaults)

  useEffect(() => {
    // Only reset if resetDefaults has changed after the initial mount
    // This handles cases where persisted values update asynchronously (e.g., from storage events)
    if (resetDefaults && prevResetDefaultsRef.current !== resetDefaults) {
      const mergedValues = { ...schemaDefaults, ...resetDefaults }
      reset(mergedValues)
    }
    prevResetDefaultsRef.current = resetDefaults
  }, [resetDefaults, reset, schemaDefaults])

  const onSubmit = (data: FormValues) => {
    const cleanedData = cleanupFormData(data)
    submitHandler(cleanedData as FormValues)
  }

  const submitButton = isSubmitting ? <LoadingButton /> : <ColumnSubmitButton />

  const fieldsToRender = useMemo(
    () => properties.filter((property) => unPinnedFilters.map((f) => f.id).includes(property.id)),
    [properties, unPinnedFilters]
  )

  const visibleFieldsToRender = useMemo(
    () =>
      fieldsToRender.filter((property) =>
        isFieldVisible(property, (watchedValues ?? {}) as Record<string, unknown>, visibleWhenMatchedIds)
      ),
    [fieldsToRender, watchedValues, visibleWhenMatchedIds]
  )

  const formInner = (
    <form onSubmit={form.handleSubmit(onSubmit)} className={cn('w-full max-w-2xl space-y-8 pt-4', className)}>
      {visibleFieldsToRender.map((property) => {
        const filter = unPinnedFilters.find((f) => f.id === property.id)
        return (
          <div className="flex items-center gap-x-2" key={filter?.id}>
            {isConfiguringLayout && <PinFilterMenu filter={filter} pinFilter={pinFilter} />}
            <FieldRenderer field={property} control={form.control} variant="column" />
          </div>
        )
      })}
      {closeSheetOnSubmit && form.formState.isValid ? (
        <SheetTrigger asChild>{submitButton}</SheetTrigger>
      ) : (
        submitButton
      )}
    </form>
  )

  return (
    <div className={cn('flex flex-col', embedInPopover ? '' : 'h-full')}>
      <Form {...form}>
        {embedInPopover ? formInner : <ScrollArea className="h-full">{formInner}</ScrollArea>}
      </Form>
    </div>
  )
})

export function LoadingButton() {
  return (
    <div className="flex w-full justify-center">
      <SpinnerIosRegular fontSize={20} className="animate-spin" />
    </div>
  )
}

export function ColumnSubmitButton() {
  const { isConfiguringLayout } = useAppPageState()
  return (
    <Button size="sm" type="submit" disabled={isConfiguringLayout}>
      Apply
    </Button>
  )
}
